// The office dashboard "launchpad" (Session ADC): the block registry and the pure layout
// helpers.
//
// Every dashboard block is REGISTERED here with a stable key, so the office can toggle, reorder
// and (from Loop 3) resize blocks in customise mode. The arranged layout is ONE shared record
// per tenant/office, persisted server-side on TenantConfig.dashboardLayout (never in browser
// storage) as the versioned descriptor `DashboardLayoutConfig`:
//
//   { version: 1, blocks: [{ key, visible, span }], pins: ["/office/sites", ...] }
//
// Array order = position. `span` is the block's width on the lg+ grid ("full" = both columns,
// "half" = one). `pins` are left-menu shortcuts (nav `to` paths, in order) rendered by the
// pinned-shortcuts block. The server validates SHAPE only; this module owns the key list and
// applies a saved layout drift-gracefully (the applyNavLayout pattern): an unknown saved key is
// dropped, a registry block missing from the saved layout is appended (visible, default span),
// a duplicate key first-wins, and pins are filtered to real, pinnable nav items. So a block or
// route added or retired after the office saved is never lost and never crashes the dashboard.
import { OFFICE_NAV, officeNavEntries, type NavItem } from "./nav";
import type { DashboardLayoutConfig, DashboardBlockSpan } from "./api";

export type DashboardBlockKey =
  | "quick-actions"
  | "pinned-shortcuts"
  | "kpi-tiles"
  | "outstanding-jobs"
  | "active-quotes"
  | "standby"
  | "office-notices";

export interface DashboardBlockDef {
  key: DashboardBlockKey;
  // Plain-English name + one-line hint shown in customise mode (the office manager reads these).
  label: string;
  hint: string;
}

// The registry, in DEFAULT order = today's dashboard exactly. The pinned-shortcuts block shows
// a one-line empty-state hint until a shortcut is pinned (Session AEA superseded the ADC
// render-nothing rule: a block that leaves no trace is a block nobody discovers).
//
// "hot-prospects" was retired in Session AKF (18 Aug 2026, Director decision): the Prospects
// feature it linked into is retired from ELE's nav (confirmed dead, zero real usage), so the
// widget's only action -- a link to a prospect's detail page -- would 404. Dropping the key here
// is enough: applyDashboardLayout already treats an unknown saved key as retired and drops it, so
// an office layout that had it pinned/ordered degrades gracefully, no migration needed.
export const DASHBOARD_BLOCKS: DashboardBlockDef[] = [
  { key: "quick-actions", label: "Quick actions", hint: "One-click buttons for the most common daily jobs" },
  { key: "pinned-shortcuts", label: "Pinned shortcuts", hint: "Your own shortcut buttons to pages from the left menu" },
  { key: "kpi-tiles", label: "Business tiles", hint: "Only the figures that need attention now, most urgent first; every figure is on Priorities" },
  { key: "outstanding-jobs", label: "Outstanding jobs", hint: "Scheduled and in-progress work, most urgent first" },
  { key: "active-quotes", label: "Active quotes and projects", hint: "Maintenance quotes and project quotations still in play" },
  // Session ANY. Appended rather than inserted near the top on purpose: applyDashboardLayout places
  // a newly registered block AFTER everything a saved layout already positions, so a block added
  // here can never reorder the dashboard the office arranged. They can move it up themselves.
  { key: "standby", label: "On standby", hint: "Who is on call this week and next, from the standby rota" },
  // Session ATZ (email system Phase 2, Item 1): the platform's notices to the office (a quote accepted, a contract signed, a
  // draft invoice raised or not), which used to arrive as emails to the office's own inbox. Appended for the same reason
  // as standby above; the office can move it up.
  { key: "office-notices", label: "Office notices", hint: "What the platform needs you to know: quotes accepted, contracts signed, invoices raised and more" },
];

const BLOCK_KEYS = new Set<string>(DASHBOARD_BLOCKS.map((b) => b.key));

export interface DashboardBlockPlacement {
  key: DashboardBlockKey;
  visible: boolean;
  span: DashboardBlockSpan;
}

// The resolved, render-ready layout: every registered block exactly once, in order, plus the
// valid pinned shortcuts.
export interface ResolvedDashboardLayout {
  blocks: DashboardBlockPlacement[];
  pins: string[];
}

const DEFAULT_SPAN: DashboardBlockSpan = "full";

export function defaultDashboardLayout(): ResolvedDashboardLayout {
  return {
    blocks: DASHBOARD_BLOCKS.map((b) => ({ key: b.key, visible: true, span: DEFAULT_SPAN })),
    pins: [],
  };
}

// The left-menu items the office can pin as dashboard shortcuts: every OFFICE_NAV item except
// the dashboard itself (a dashboard shortcut on the dashboard is noise). Pure.
export function pinnableNavItems(): NavItem[] {
  // SC7A: the menu is 14 hubs and every other page is a tab, so a shortcut can point at a tab too
  // (officeNavEntries), which also keeps every shortcut pinned before the calm menu valid.
  return officeNavEntries().filter((item) => item.to !== "/office");
}

// A page that used to be its own menu item and is now an alias (`also`) of a hub or one of its tabs: the path the shortcut belongs on now.
// Null when nothing in the menu claims the path (a retired page). Pure.
export function pinHomeFor(path: string): string | null {
  for (const section of OFFICE_NAV) {
    for (const item of section.items) {
      for (const tab of item.tabs ?? []) if (tab.also?.includes(path)) return tab.to;
    }
  }
  return null;
}

// Look up a pinnable nav item by its `to` path (the pin key), for rendering a pinned shortcut's
// label/hint. Null for a path that is not a pinnable item (a removed route, or "/office"). Pure.
export function navItemByPath(to: string): NavItem | null {
  return pinnableNavItems().find((item) => item.to === to) ?? null;
}

// The pinnable items grouped by their left-menu section, for the customise-mode "Pin a page"
// picker (an optgroup per section, mirroring the sidebar the office already knows). A section
// whose only item is the dashboard drops out entirely. Pure.
export function pinnableNavSections(): { label: string; items: NavItem[] }[] {
  const entries = officeNavEntries();
  return OFFICE_NAV.map((section) => ({
    label: section.label,
    items: entries.filter((e) => e.section === section.label && e.to !== "/office"),
  })).filter((section) => section.items.length > 0);
}

// Apply a saved layout descriptor over the registry, drift-gracefully (see the header comment).
// Null/undefined (no saved layout, or the server coerced a corrupt row away) = the default.
// Defensive on top of the server's shape validation: an unknown span falls back to "full" and a
// non-boolean visible to true, so a hand-edited row still renders every block. Pure.
export function applyDashboardLayout(saved: DashboardLayoutConfig | null | undefined): ResolvedDashboardLayout {
  if (!saved || saved.version !== 1 || !Array.isArray(saved.blocks)) return defaultDashboardLayout();

  const placed = new Set<string>();
  const blocks: DashboardBlockPlacement[] = [];
  for (const b of saved.blocks) {
    if (!b || typeof b.key !== "string") continue;
    if (!BLOCK_KEYS.has(b.key) || placed.has(b.key)) continue; // drop a retired/unknown or duplicate key
    placed.add(b.key);
    blocks.push({
      key: b.key as DashboardBlockKey,
      visible: typeof b.visible === "boolean" ? b.visible : true,
      span: b.span === "half" ? "half" : "full",
    });
  }
  // Append any registered block the saved layout does not place (a block added after the office
  // saved), visible in its default position relative to the other appended blocks.
  for (const def of DASHBOARD_BLOCKS) {
    if (!placed.has(def.key)) {
      placed.add(def.key);
      blocks.push({ key: def.key, visible: true, span: DEFAULT_SPAN });
    }
  }

  const pinnable = new Set(pinnableNavItems().map((i) => i.to));
  const pins: string[] = [];
  const seenPins = new Set<string>();
  for (const raw of Array.isArray(saved.pins) ? saved.pins : []) {
    if (typeof raw !== "string") continue;
    // A shortcut pinned before the calm menu may name a page that is now an alias of a hub or tab (/office/nps): keep it, on its new home.
    const pin = pinnable.has(raw) ? raw : pinHomeFor(raw);
    if (!pin || !pinnable.has(pin) || seenPins.has(pin)) continue; // drop removed/duplicate
    seenPins.add(pin);
    pins.push(pin);
  }

  return { blocks, pins };
}

// The blocks after a customise-mode drag: the active block moves to the over block's position
// (dnd-kit arrayMove semantics -- the target index is measured in the ORIGINAL array, so the
// removal shift never lands a downward drag one slot short; the Session AD nav fix). A drop on
// itself or an unknown key changes nothing. Pure (no dnd-kit dependency), so the persisted
// order is unit-testable without simulating a real drag. Mirrors applyNavDragEnd.
export function applyBlockDragEnd(
  blocks: DashboardBlockPlacement[],
  activeKey: string,
  overKey: string,
): { blocks: DashboardBlockPlacement[]; changed: boolean } {
  if (activeKey === overKey) return { blocks, changed: false };
  const fromIdx = blocks.findIndex((b) => b.key === activeKey);
  const toIdx = blocks.findIndex((b) => b.key === overKey);
  if (fromIdx === -1 || toIdx === -1) return { blocks, changed: false };
  const next = blocks.slice();
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved!);
  return { blocks: next, changed: true };
}

// The descriptor to persist for a resolved layout (what PUT /api/dashboard/layout sends). Pure.
export function dashboardLayoutToStore(resolved: ResolvedDashboardLayout): DashboardLayoutConfig {
  return {
    version: 1,
    blocks: resolved.blocks.map((b) => ({ key: b.key, visible: b.visible, span: b.span })),
    pins: [...resolved.pins],
  };
}

// True when the resolved layout IS the default (every block visible, full width, default order,
// no pins) -- lets the UI show "Reset to default" as a no-op state if wanted. Pure.
export function isDefaultDashboardLayout(resolved: ResolvedDashboardLayout): boolean {
  if (resolved.pins.length > 0) return false;
  if (resolved.blocks.length !== DASHBOARD_BLOCKS.length) return false;
  return resolved.blocks.every(
    (b, i) => b.key === DASHBOARD_BLOCKS[i]!.key && b.visible && b.span === DEFAULT_SPAN,
  );
}
